import QRCode from 'qrcode';
import type { DevQrInjectMode } from '../store/dev-qr-inject-store';

interface PayloadContext {
  containerId: string | null | undefined;
  sequence: number;
}

/**
 * Derive the QR string to bake into the image for a given injection mode.
 * Returns `null` when no injection should happen (caller is not dev-tools
 * enabled, mode `off`, or missing context). Callers must pass `enabled`
 * derived from `isDevToolsAllowedForEmail(user?.email)` so a stale persisted
 * mode can never leak into non-admin uploads.
 */
export function buildInjectedPayload(
  enabled: boolean,
  mode: DevQrInjectMode,
  ctx: PayloadContext
): string | null {
  if (!enabled || mode === 'off') return null;

  const seq = ctx.sequence;
  const container =
    ctx.containerId && ctx.containerId !== '—' ? ctx.containerId : 'CNT-DEV';

  if (mode === 'match') return `${container}-P${seq}`;
  if (mode === 'mismatch') return `OTHER-CONTAINER-P${seq}`;
  if (mode === 'garbage') return 'NOT_A_PAGE_QR_DEV_INJECT';
  return null;
}

/** A4 short edge in mm — used to map a physical label size to a fraction of
 *  the scanned page. For an upright A4 scan the image's short side spans this
 *  many mm at ANY DPI, so `labelMm / A4_SHORT_EDGE_MM` is the exact on-page
 *  fraction the printed label occupies (and stays correct across resolutions). */
const A4_SHORT_EDGE_MM = 210;
/** Decode-safety floor (px): never render a QR so small the decoder chokes,
 *  even if the configured label is tiny relative to a low-res image. */
const MIN_QR_PX = 80;

export interface InjectQrOptions {
  /**
   * Physical label edge in mm (from the "Label dimensions" configuration).
   * When provided, the injected QR is sized to occupy the same fraction of the
   * page a real printed label would — `labelMm / pageShortMm` of the image's
   * short side — instead of the legacy fixed 18%. QR symbols are square, so
   * callers pass the smaller of label width/height.
   */
  labelMm?: number;
  /** Physical short edge of the scanned page in mm. Defaults to A4 (210mm). */
  pageShortMm?: number;
}

/**
 * Returns a new Blob with the source image rasterized to a canvas and a QR
 * code overlaid in the bottom-right corner.
 *
 * Sizing: when `opts.labelMm` is supplied the QR is drawn at the configured
 * physical label size — `shortSide × labelMm / pageShortMm` px — so a dev-
 * injected QR matches the footprint of a real printed sticker on the page.
 * Without it, the QR falls back to ~18% of the shorter image dimension.
 *
 * Dev-only: callers must already have gated on the dev-tools email allowlist
 * via `buildInjectedPayload(enabled, …)`. The function still no-ops if the
 * payload is empty.
 */
export async function injectQrIntoBlob(
  source: Blob,
  payload: string | null,
  opts?: InjectQrOptions
): Promise<Blob> {
  if (!payload) return source;

  const bitmap = await blobToImageBitmap(source);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return source;
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close?.();

  const shortSide = Math.min(canvas.width, canvas.height);
  const labelMm = opts?.labelMm;
  const pageShortMm = opts?.pageShortMm ?? A4_SHORT_EDGE_MM;
  const qrSize =
    typeof labelMm === 'number' && labelMm > 0 && pageShortMm > 0
      ? // Physical label size mapped onto the page (matches a real sticker).
        Math.max(MIN_QR_PX, Math.round(shortSide * (labelMm / pageShortMm)))
      : // Legacy fallback when no label config is available.
        Math.max(120, Math.round(shortSide * 0.18));
  // White margin pad around the QR so the decoder has a clear quiet zone even
  // when the underlying photo is dark or busy.
  const pad = Math.round(qrSize * 0.08);

  const qrDataUrl = await QRCode.toDataURL(payload, {
    width: qrSize,
    margin: 1,
    errorCorrectionLevel: 'M'
  });
  const qrImg = await loadImage(qrDataUrl);

  const x = canvas.width - qrSize - pad * 2;
  const y = canvas.height - qrSize - pad * 2;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x - pad, y - pad, qrSize + pad * 2, qrSize + pad * 2);
  ctx.drawImage(qrImg, x, y, qrSize, qrSize);

  const mime =
    source.type && source.type.startsWith('image/')
      ? source.type
      : 'image/jpeg';
  return new Promise<Blob>((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob ?? source),
      mime,
      mime === 'image/jpeg' ? 0.92 : undefined
    );
  });
}

/**
 * Wraps a `File` so the consumer's mime/name metadata survives the canvas
 * round-trip. Returns the original File unchanged when injection is disabled.
 */
export async function injectQrIntoFile(
  file: File,
  payload: string | null,
  opts?: InjectQrOptions
): Promise<File> {
  if (!payload) return file;
  const blob = await injectQrIntoBlob(file, payload, opts);
  return new File([blob], file.name, {
    type: blob.type || file.type,
    lastModified: file.lastModified
  });
}

async function blobToImageBitmap(blob: Blob): Promise<ImageBitmap> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(blob);
  }
  // Fallback for browsers without `createImageBitmap` (rare on dev targets).
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    return (await createImageBitmap(canvas)) as ImageBitmap;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
